import {
  Button,
  ButtonLevel,
  ButtonSize,
  Menu,
  MenuItem,
  MenuPopover,
  MenuTrigger,
  Skeleton,
  Text,
  TextColor,
  TextSize,
  TextWeight,
  Tooltip,
  TooltipTrigger,
} from '@vizit/atoms';
import { DotsVerticalIcon, DownloadMinimalIcon, RefreshCw04Icon } from '@vizit/icons';
import * as React from 'react';
import { productCountClasses } from './product-listing-table.classes';
import { NewProductMenuButton } from './product-listing-table.new-product-button';

interface ProductListingTableHeaderProps {
  /**
   * Total count of product pages to display
   */
  totalCount?: number;
  /**
   * Whether the count is loading
   */
  isLoading?: boolean;
  /**
   * Dialog component for creating a new product page
   */
  createProductDialog?: React.ReactNode;
  /**
   * Dialog component for importing a product page via URL
   */
  importProductDialog?: React.ReactNode;
  /**
   * Dialog component for bulk importing product pages via CSV
   */
  bulkImportDialog?: React.ReactNode;
  /**
   * Callback executed when the Download CSV button is pressed
   */
  onDownloadCsv?: () => void;
  /**
   * Whether the download action is currently running
   */
  isDownloadingCsv?: boolean;
  /**
   * Whether the download button should be disabled
   */
  isDownloadCsvDisabled?: boolean;
  /**
   * Callback executed when the Refresh products button is pressed
   */
  onRefreshProducts?: () => void;
  /**
   * Whether the refresh feature flag is enabled
   */
  isRefreshFeatureEnabled?: boolean;
  /**
   * Whether products are currently being refreshed
   */
  isRefreshing?: boolean;
}

const ProductListingTableHeader = React.memo((props: ProductListingTableHeaderProps) => {
  const {
    totalCount,
    isLoading = false,
    createProductDialog,
    importProductDialog,
    bulkImportDialog,
    onDownloadCsv,
    isDownloadingCsv = false,
    isDownloadCsvDisabled = false,
    onRefreshProducts,
    isRefreshFeatureEnabled = false,
    isRefreshing = false,
  } = props;

  const showOverflowMenu = onDownloadCsv || (onRefreshProducts && isRefreshFeatureEnabled);

  if (isLoading) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4 w-full">
        <Skeleton
          className={productCountClasses()}
          variant="square"
          data-testid="product-listing-table-header-count-skeleton"
        />
        <Skeleton
          className="h-20 w-64 mt-5"
          variant="square"
          data-testid="product-listing-table-header-menu-skeleton"
        />
      </div>
    );
  }

  const countText =
    totalCount !== undefined
      ? totalCount === 1
        ? `Showing ${totalCount} product page`
        : `Showing ${totalCount} product pages`
      : '';

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 w-full">
      <div className="flex items-center gap-2">
        <Text className="text-left w-fit" size={TextSize.Sm} color={TextColor.Tertiary} weight={TextWeight.Medium}>
          {totalCount ? countText : null}
        </Text>
        {isRefreshing && (
          <TooltipTrigger>
            <Button
              isDisabled={true}
              level={ButtonLevel.Ghost}
              size={ButtonSize.Xs}
              iconStart={
                <RefreshCw04Icon aria-label="Refreshing all product pages" size={16} className="animate-pulse" />
              }
            />
            <Tooltip>All product pages are being refreshed</Tooltip>
          </TooltipTrigger>
        )}
      </div>
      <div className="flex items-center gap-2">
        <NewProductMenuButton
          createProductDialog={createProductDialog}
          importProductDialog={importProductDialog}
          bulkImportDialog={bulkImportDialog}
        />
        {showOverflowMenu && (
          <MenuTrigger>
            <Button
              level={ButtonLevel.Secondary}
              size={ButtonSize.Lg}
              iconStart={<DotsVerticalIcon aria-label="Actions menu" size={20} />}
              className="rounded-none"
              data-testid="library-products-actions-menu"
            />
            <MenuPopover placement="bottom end">
              <Menu>
                {onDownloadCsv && (
                  <MenuItem
                    onAction={onDownloadCsv}
                    isDisabled={isDownloadCsvDisabled || isDownloadingCsv}
                    textValue="Download CSV"
                  >
                    <div className="flex items-center gap-5">
                      <DownloadMinimalIcon aria-hidden="true" size={14} />
                      <Text size={TextSize.Md} color={TextColor.Inherit}>
                        Download CSV
                      </Text>
                    </div>
                  </MenuItem>
                )}
                {onRefreshProducts && isRefreshFeatureEnabled && (
                  <MenuItem onAction={onRefreshProducts} textValue="Refresh products" isDisabled={isRefreshing}>
                    <div className="flex items-center gap-5">
                      <RefreshCw04Icon aria-hidden="true" size={14} />
                      <Text size={TextSize.Md} color={TextColor.Inherit}>
                        Refresh all product pages
                      </Text>
                    </div>
                  </MenuItem>
                )}
              </Menu>
            </MenuPopover>
          </MenuTrigger>
        )}
      </div>
    </div>
  );
});

export { ProductListingTableHeader };
export type { ProductListingTableHeaderProps };
